import React from 'react';
import { X, Check, AlertCircle, LightbulbIcon, Star, CalendarDays } from 'lucide-react';
import { AIReferenceEvaluation } from '../../api/candidates';
import { useLanguage } from '../../contexts/LanguageContext';
import useEscapeKey from '../../hooks/useEscapeKey';

interface AIEvaluationModalProps {
  evaluation: AIReferenceEvaluation;
  candidateName: string;
  isOpen: boolean;
  onClose: () => void;
}

const AIEvaluationModal: React.FC<AIEvaluationModalProps> = ({
  evaluation,
  candidateName,
  isOpen,
  onClose
}) => {
  const { t } = useLanguage();
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose, isOpen);
  
  if (!isOpen) return null;
  
  // Helper function to render list sections
  const renderList = (items: string[], icon: React.ReactNode, titleKey: string) => {
    const title = t(`candidates.referenceModals.aiEvaluationModal.keySections.${titleKey}`);
    
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        <ul className="ml-8 space-y-2">
          {items.map((item, index) => (
            <li key={index} className="text-gray-700 flex items-start">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2"></span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  };
  
  // Create a fit indicator based on fit_value (0-100)
  const getFitIndicator = () => {
    const { fit_value } = evaluation.evaluation;
    let color = 'bg-red-500';
    
    if (fit_value >= 80) color = 'bg-green-500';
    else if (fit_value >= 60) color = 'bg-emerald-400';
    else if (fit_value >= 40) color = 'bg-amber-400';
    else if (fit_value >= 20) color = 'bg-orange-500';
    
    return (
      <div className="flex items-center gap-2 mb-2">
        <div className="h-12 w-12 rounded-full flex items-center justify-center"
             style={{ backgroundColor: color }}>
          <span className="text-white font-bold">{fit_value}%</span>
        </div>
        <div className="flex flex-col">
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full" 
              style={{ width: `${fit_value}%`, backgroundColor: color }}
            />
          </div>
          <span className="font-medium mt-1">{evaluation.evaluation.fit_description}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('candidates.referenceModals.aiEvaluationModal.title', { name: candidateName })}
            </h2>
            <div className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md text-sm font-semibold">
              {evaluation.evaluation.fit_value}% {t('candidates.referenceModals.aiEvaluationModal.match')}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Fit indicator */}
          <div className="mb-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">
              {t('candidates.referenceModals.aiEvaluationModal.candidateFit')}
            </h3>
            {getFitIndicator()}
          </div>
          
          {/* Summary */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">
              {t('candidates.referenceModals.aiEvaluationModal.summary')}
            </h3>
            <p className="text-gray-700">{evaluation.evaluation.summary}</p>
          </div>
          
          {/* Lists */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              {renderList(
                evaluation.insights.key_considerations,
                <AlertCircle className="w-5 h-5 text-amber-500" />,
                "keyConsiderations"
              )}
            </div>
            <div>
              {renderList(
                evaluation.insights.positive_highlights,
                <Star className="w-5 h-5 text-emerald-500" />,
                "positiveHighlights"
              )}
            </div>
          </div>
          
          {renderList(
            evaluation.insights.first_days_initiatives,
            <CalendarDays className="w-5 h-5 text-blue-500" />,
            "firstDaysInitiatives"
          )}
          
          {/* Conclusion */}
          <div className="mt-4 bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">
              {t('candidates.referenceModals.aiEvaluationModal.keySections.conclusion')}
            </h3>
            <p className="text-gray-700">{evaluation.evaluation.conclusion}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIEvaluationModal;